"use client"

import { useEffect } from "react"
import { Label, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@ui/shad"

import { usePhoneIntegrations } from "./use-phone-integrations"

interface SmsIntegrationFieldProps {
  value: string | undefined
  onChange: (value: string) => void
  label?: string
  required?: boolean
  autoSelect?: boolean
}

export function SmsIntegrationField({
  value,
  onChange,
  label = "Send from",
  required,
  autoSelect = true,
}: SmsIntegrationFieldProps) {
  const { options: integrationOptions, isLoading } = usePhoneIntegrations()

  useEffect(() => {
    if (!autoSelect) return
    if (!value && integrationOptions.length > 0 && !isLoading) {
      onChange(integrationOptions[0].value)
    }
  }, [autoSelect, integrationOptions, value, onChange, isLoading])

  const selectedOption = value ? integrationOptions.find((opt) => opt.value === value) : null
  const hasOptions = integrationOptions.length > 0

  return (
    <div className="space-y-2">
      <Label className="text-sm font-medium" htmlFor="sms_integration">
        {label}
        {required && <span className="text-red-accent ml-1">*</span>}
      </Label>
      <Select value={selectedOption?.value} onValueChange={onChange} disabled={!hasOptions && !isLoading}>
        <SelectTrigger id="sms_integration" className="h-fit w-full py-2">
          {selectedOption ? (
            <SelectValue asChild>
              <div className="text-left">
                <p className="text-foreground text-sm font-medium">{selectedOption.label}</p>
                {selectedOption.detail && <p className="text-muted-foreground text-xs">{selectedOption.detail}</p>}
              </div>
            </SelectValue>
          ) : (
            <SelectValue
              placeholder={
                hasOptions
                  ? "Select a phone integration"
                  : isLoading
                    ? "Loading integrations..."
                    : "No phone integrations available"
              }
            />
          )}
        </SelectTrigger>
        <SelectContent>
          {integrationOptions.map((option) => (
            <SelectItem key={option.value} value={option.value} textValue={option.label} className="py-2">
              <div className="text-left">
                <p className="text-foreground text-sm font-medium">{option.label}</p>
                {option.detail && <p className="text-muted-foreground text-xs">{option.detail}</p>}
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {!hasOptions && !isLoading && (
        <p className="text-muted-foreground text-xs">
          Connect a phone integration (CurrentClient, RingCentral, MyRepChat, Dialpad, Zoom Phone, or Intulse) to send
          SMS from this automation.
        </p>
      )}
    </div>
  )
}
